<section class="jobs-console" [class.jobs-console--compact]="compact" aria-live="polite">
  @if (loading && !overview) {
    <div class="jobs-loading"><span class="spinner-border spinner-border-sm"></span></div>
  } @else if (overview) {
    <header class="jobs-header">
      <div>
        <span class="eyebrow">{{ 'AMAZON_FINANCE_AUTOMATION' | translate }}</span>
        <h2>{{ 'AMAZON_FINANCE_PIPELINE' | translate }}</h2>
        <p>
          @if (overview.running) {
            <span class="pipeline-state pipeline-state--running">
              <i class="fa-solid fa-circle-notch fa-spin"></i>
              {{ 'AMAZON_FINANCE_JOB_STAGE_' + overview.runningStage | translate }}
              {{ 'RUNNING_LOWER' | translate }}
            </span>
          } @else {
            <span class="pipeline-state pipeline-state--idle">
              <i class="fa-solid fa-circle-check"></i> {{ 'AMAZON_FINANCE_PIPELINE_IDLE' | translate }}
            </span>
          }
        </p>
        @if (runningMessage()) {
          <div class="running-message">
            <ng-container *ngTemplateOutlet="jobMessage; context: { $implicit: runningMessage() }"></ng-container>
          </div>
        }
      </div>
      <div class="jobs-header__actions">
        @if (compact) {
          <button type="button" class="btn btn-link btn-sm" (click)="openDetails.emit()">
            {{ 'VIEW_JOB_DETAILS' | translate }} <i class="fa-solid fa-arrow-right"></i>
          </button>
        } @else {
          <button type="button" class="btn btn-outline-secondary btn-sm" (click)="refresh()" [disabled]="loading">
            <i class="fa-solid fa-rotate" [class.fa-spin]="loading"></i> {{ 'REFRESH' | translate }}
          </button>
        }
        <button type="button" class="btn btn-primary btn-sm" (click)="run('FULL_PIPELINE')" [disabled]="!canRun()">
          @if (actionStage === 'FULL_PIPELINE') {
            <span class="spinner-border spinner-border-sm"></span>
          } @else {
            <i class="fa-solid fa-play"></i>
          }
          {{ 'RUN_FULL_PIPELINE' | translate }}
        </button>
      </div>
    </header>

    @if (error) {
      <div class="alert alert-danger py-2">{{ error | translate }}</div>
    }
    @if (notice) {
      <div class="alert alert-success py-2">{{ notice }}</div>
    }

    <div class="stage-flow">
      @for (job of stageJobs(); track job.stage; let last = $last) {
        <article class="stage-card" [class.stage-card--running]="job.running">
          <div class="stage-card__top">
            <span class="stage-number">{{ $index + 1 }}</span>
            <span class="job-status" [ngClass]="statusClass(status(job))">
              {{ 'AMAZON_FINANCE_JOB_STATUS_' + status(job) | translate }}
            </span>
          </div>
          <h3>{{ 'AMAZON_FINANCE_JOB_STAGE_' + job.stage | translate }}</h3>
          @if (!compact) {
            <p class="schedule">
              <span>{{ 'AMAZON_FINANCE_JOB_SCHEDULE_' + job.scheduleType | translate }}</span>
              @if (job.cron) {
                <code>{{ job.cron }}</code>
              }
            </p>
            <dl>
              <div>
                <dt>{{ 'LAST_RUN' | translate }}</dt>
                <dd>
                  {{ job.latestRun?.startTime ? (job.latestRun?.startTime | date: 'short') : ('NEVER' | translate) }}
                </dd>
              </div>
              <div>
                <dt>{{ 'NEXT_RUN' | translate }}</dt>
                <dd>{{ job.nextFireTime ? (job.nextFireTime | date: 'short') : '—' }}</dd>
              </div>
            </dl>
            @if (job.latestRun?.message) {
              <ng-container
                *ngTemplateOutlet="jobMessage; context: { $implicit: job.latestRun?.message }"
              ></ng-container>
            }
            <button
              type="button"
              class="btn btn-outline-primary btn-sm run-stage"
              (click)="run(job.stage)"
              [disabled]="!canRun()"
            >
              @if (actionStage === job.stage) {
                <span class="spinner-border spinner-border-sm"></span>
              } @else {
                <i class="fa-solid fa-play"></i>
              }
              {{ 'RUN_NOW' | translate }}
            </button>
          }
        </article>
        @if (!last) {
          <i class="fa-solid fa-arrow-right stage-arrow" aria-hidden="true"></i>
        }
      }
    </div>

    @if (!compact) {
      <section class="history-card">
        <div class="history-card__header">
          <div>
            <span class="eyebrow">{{ 'AMAZON_FINANCE_JOB_HISTORY' | translate }}</span>
            <h3>{{ 'RECENT_EXECUTIONS' | translate }}</h3>
          </div>
          <small>{{ 'AUTO_REFRESH_5_SECONDS' | translate }}</small>
        </div>
        @if (latestRuns().length) {
          <div class="table-responsive">
            <table class="table align-middle mb-0">
              <thead>
                <tr>
                  <th>{{ 'STAGE' | translate }}</th>
                  <th>{{ 'STATUS' | translate }}</th>
                  <th>{{ 'STARTED' | translate }}</th>
                  <th>{{ 'DURATION' | translate }}</th>
                  <th>{{ 'STARTED_BY' | translate }}</th>
                  <th>{{ 'MESSAGE' | translate }}</th>
                </tr>
              </thead>
              <tbody>
                @for (item of latestRuns(); track item.run.id) {
                  <tr>
                    <td>{{ 'AMAZON_FINANCE_JOB_STAGE_' + item.stage | translate }}</td>
                    <td>
                      <span class="job-status" [ngClass]="statusClass(item.run.status)">
                        {{ 'AMAZON_FINANCE_JOB_STATUS_' + item.run.status | translate }}
                      </span>
                    </td>
                    <td>{{ item.run.startTime | date: 'short' }}</td>
                    <td class="duration-cell">{{ formatDuration(item.run.executionTime) }}</td>
                    <td>
                      {{ item.run.manual ? item.run.requestedBy || ('MANUAL' | translate) : ('SCHEDULED' | translate) }}
                    </td>
                    <td class="history-message">
                      <ng-container
                        *ngTemplateOutlet="jobMessage; context: { $implicit: item.run.message }"
                      ></ng-container>
                      @if (item.run.exception) {
                        <details>
                          <summary>{{ 'SHOW_ERROR' | translate }}</summary>
                          <pre>{{ item.run.exception }}</pre>
                        </details>
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        } @else {
          <p class="empty-state">{{ 'NO_JOB_HISTORY' | translate }}</p>
        }
      </section>
    }
  } @else if (error) {
    <div class="alert alert-danger">{{ error | translate }}</div>
  }
</section>

<ng-template #jobMessage let-raw>
  @let message = formatMessage(raw);
  <div class="job-message">
    @if (message.summaryKey) {
      <strong>{{ message.summaryKey | translate }}</strong>
    } @else {
      <div class="job-message__summary">{{ message.summary }}</div>
    }
    @if (message.current !== null && message.total !== null) {
      <div class="job-message__progress-label">
        {{
          'JOB_MESSAGE_PROCESSING' | translate: { current: (message.current | number), total: (message.total | number) }
        }}
      </div>
      @if (message.percent !== null) {
        <progress
          class="job-message__progress"
          [value]="message.current"
          [max]="message.total"
          [attr.aria-label]="'JOB_MESSAGE_PROGRESS' | translate"
        ></progress>
      }
    }
    @if (message.counts.length) {
      <ul class="job-message__counts">
        @for (entry of message.counts; track entry.status) {
          <li
            [class.job-message__count--success]="entry.status === 'MAPPED'"
            [class.job-message__count--error]="
              entry.status === 'EXCEPTION' || entry.status === 'MAPPED_ORDER_LOOKUP_FAILED'
            "
            [title]="entry.status"
          >
            <span>{{
              messageStatuses[entry.status] ? (messageStatuses[entry.status] | translate) : entry.status
            }}</span>
            <strong>{{ entry.count | number }}</strong>
          </li>
        }
      </ul>
    }
    @if (message.formatted) {
      <details class="job-message__raw">
        <summary>{{ 'JOB_MESSAGE_ORIGINAL' | translate }}</summary>
        <pre>{{ message.original }}</pre>
      </details>
    }
  </div>
</ng-template>
